'use client';

import { useClerk, useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/nextjs';
import { signOut as firebaseSignOut, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import type { ReactNode } from 'react';
import { globalStore } from '@/app/globalStore';
import { auth } from '@/firebase/config';
import { useAuthToken } from '@/hooks/useAuthToken';
import { requestSentryNextjsUserIdentity } from '@/lib/sentry/applySentryVersionTags';
export type UserInfo = {
  userName: string | null;
  email: string | null;
  imageUrl: string | null;
  fullName: string | null;
};
interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  userInfo: UserInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  tokenVerified: boolean;
  isAuthReady: boolean;
}
const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  userInfo: null,
  login: async () => {},
  logout: async () => {},
  isLoggingIn: false,
  isLoggingOut: false,
  tokenVerified: false,
  isAuthReady: false
});
export const useAuth = () => {
  return useContext(AuthContext);
};
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null | undefined>(undefined);
  const {
    getToken: getClerkToken,
    userId: clerkUserId,
    isLoaded: clerkIsLoaded
  } = useClerkAuth();
  const {
    user: clerkUser = null
  } = useClerkUser();
  const {
    signOut: clerkSignOut
  } = useClerk();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);

  // Track token setting attempts to prevent infinite loops
  const tokenSettingInProgress = useRef(false);
  const tokenSettingRetries = useRef(0);
  const MAX_TOKEN_RETRIES = 3;
  const {
    refreshAndSetToken,
    clearAuthToken
  } = useAuthToken();

  // Authentication is ready when both Clerk and Firebase have initialized
  const isAuthReady = clerkIsLoaded && firebaseInitialized;

  // Debug Clerk user state changes (only log when loaded)
  useEffect(() => {
    if (clerkIsLoaded) {
      console.log('[auth] 🔍 Clerk ready:', {
        hasUser: !!clerkUserId,
        email: clerkUser?.emailAddresses?.[0]?.emailAddress,
        fullName: clerkUser?.fullName
      });
    }
  }, [clerkIsLoaded, clerkUserId, clerkUser?.emailAddresses, clerkUser?.fullName]);

  // Update the firebase user based on auth state change
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth(), async firebaseUser_0 => {
      try {
        console.log('[auth] ✅ Firebase auth state changed:', !!firebaseUser_0);
        setFirebaseUser(firebaseUser_0);
        setFirebaseInitialized(true);
      } catch (error) {
        console.error('[auth] Auth state change error:', error);
        setFirebaseInitialized(true); // Still mark as initialized even on error
      }
    });
    return () => unsubscribe();
  }, []);

  // main login method called by the UI
  const login = useCallback(async () => {
    if (!clerkUserId || isLoggingIn || isLoggingOut) {
      return;
    }
    console.log('[auth] 🔐 Logging in...');
    setIsLoggingIn(true);
    try {
      // Get the Clerk session token (JWT)
      const clerkToken = await getClerkToken();
      if (!clerkToken) {
        console.error('[auth] ❌ Failed to get Clerk token');
        return;
      }

      // Exchange Clerk token for Firebase custom token via our API
      const response = await fetch('/api/auth/firebase-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: clerkToken
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error ?? 'Failed to get Firebase token');
      }
      const {
        firebaseToken
      } = await response.json();
      if (firebaseToken) {
        await signInWithCustomToken(auth(), firebaseToken);
        console.log('[auth] ✅ Successfully logged into Firebase with custom token.');
      }
    } catch (error_0) {
      console.error('[auth] ❌ Login error:', error_0);
    } finally {
      setIsLoggingIn(false);
    }
  }, [clerkUserId, isLoggingIn, isLoggingOut, getClerkToken]);

  // Automatically sign into Firebase when Clerk user is authenticated
  useEffect(() => {
    // Only trigger auto-login after both systems are initialized
    if (!isAuthReady) {
      return;
    }

    // Trigger auto-login when Clerk has user but Firebase doesn't
    if (clerkUserId && firebaseUser === null && !isLoggingIn) {
      console.log('[auth] 🔄 Auto-login triggered: Clerk authenticated, Firebase not.');
      login();
    }
  }, [clerkUserId, firebaseUser, login, isAuthReady, isLoggingIn]);

  // main logout method called by the UI
  const logout = useCallback(async () => {
    if (isLoggingIn || isLoggingOut) {
      return;
    }
    console.log('[auth] 🚪 Logging out...');
    setIsLoggingOut(true);

    // Reset token tracking
    tokenSettingRetries.current = 0;
    tokenSettingInProgress.current = false;
    try {
      // Sign out from both services
      await Promise.all([clerkSignOut({
        redirectUrl: '/auth/signin'
      }), firebaseSignOut(auth())]);
      console.log('[auth] ✅ Successfully logged out from Firebase and Clerk.');
    } catch (error_1) {
      console.error('[auth] ❌ Logout error:', error_1);
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingIn, isLoggingOut, clerkSignOut]);

  // Handle case where Firebase is authenticated but Clerk is not (session expired)
  // Only check this after BOTH systems are ready to avoid premature logouts
  useEffect(() => {
    if (!isAuthReady) {
      return; // Don't make decisions until both systems are ready
    }

    // If Firebase has user but Clerk doesn't (and we're not in the middle of logging in/out)
    if (firebaseUser && !clerkUserId && !isLoggingOut && !isLoggingIn) {
      console.log('[auth] ⚠️ Firebase authenticated but Clerk session missing - logging out.');
      logout();
    }
  }, [firebaseUser, clerkUserId, isLoggingOut, isLoggingIn, logout, isAuthReady]);

  // Retry logic for setting token with exponential backoff
  const setTokenWithRetry = useCallback(async (user: FirebaseUser, attempt = 0): Promise<boolean> => {
    if (attempt >= MAX_TOKEN_RETRIES) {
      console.error('[auth] ❌ Max token setting retries exceeded.');
      return false;
    }
    try {
      const token = await refreshAndSetToken(user);
      if (token) {
        tokenSettingRetries.current = 0; // Reset on success
        return true;
      }
      throw new Error('Failed to set token');
    } catch (error_2) {
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Exponential backoff, max 5s
      console.log(`[auth] ⏳ Token setting failed (attempt ${attempt + 1}/${MAX_TOKEN_RETRIES}), retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return setTokenWithRetry(user, attempt + 1);
    }
  }, [refreshAndSetToken, MAX_TOKEN_RETRIES]);

  // Automatically refresh the token before it expires
  useEffect(() => {
    if (!firebaseUser) {
      return;
    }

    // Refresh token every 50 minutes (before 1-hour expiry)
    const refreshInterval = setInterval(async () => {
      try {
        await refreshAndSetToken(firebaseUser);
        setTokenVerified(true);
        console.log('[auth] ✅ Successfully refreshed auth token.');
      } catch (error_3) {
        setTokenVerified(false);
        console.error('[auth] ❌ Token refresh failed:', error_3);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [firebaseUser, refreshAndSetToken]);

  // Update the auth token based on the firebase user
  useEffect(() => {
    const updateAuthToken = async () => {
      // Don't update token until auth is ready
      if (!isAuthReady) {
        return;
      }

      // Prevent concurrent token setting operations
      if (tokenSettingInProgress.current) {
        console.log('[auth] ⏳ Token setting already in progress, skipping...');
        return;
      }
      try {
        if (firebaseUser) {
          tokenSettingInProgress.current = true;

          // Use retry logic to set the auth cookie
          const success = await setTokenWithRetry(firebaseUser);
          if (success) {
            setTokenVerified(true);
            console.log('[auth] ✅ Successfully set auth token.');
          } else {
            setTokenVerified(false);
            console.warn('[auth] ⚠️ Failed to set auth cookie after retries.');
          }
        } else {
          // Clear the token when logged out
          setTokenVerified(false);
          await clearAuthToken();
          console.log('[auth] 🧹 Cleared auth token.');
        }
      } catch (error_4) {
        console.error('[auth] ❌ Failed to update auth token:', error_4);
        setTokenVerified(false);
      } finally {
        tokenSettingInProgress.current = false;
      }
    };
    updateAuthToken();
  }, [firebaseUser, clearAuthToken, setTokenWithRetry, isAuthReady]);

  // Update the user info (optimized to reduce redundant updates)
  useEffect(() => {
    const updateUserInfo = async () => {
      // Wait for auth to be ready before updating user info
      if (!isAuthReady) {
        return;
      }
      try {
        // Only proceed if Firebase user exists
        if (!firebaseUser) {
          setUserInfo(null);
          // Update global store
          globalStore.setCurrentUser(null, null);
          requestSentryNextjsUserIdentity(null, null);
          return;
        }

        // Get email from Firebase token claims if not available in profile
        let firebaseEmail = firebaseUser.email;
        if (!firebaseEmail) {
          try {
            const token_0 = await firebaseUser.getIdTokenResult();
            firebaseEmail = token_0.claims.email as string || null;
          } catch (error_6) {
            console.warn('[auth] ⚠️ Failed to get email from token claims:', error_6);
          }
        }

        // Build userInfo with Firebase data first
        let newUserInfo: UserInfo = {
          userName: firebaseUser.displayName,
          email: firebaseEmail,
          imageUrl: firebaseUser.photoURL,
          fullName: firebaseUser.displayName
        };

        // Supplement with Clerk data if available
        if (clerkUser) {
          // Better fallback logic for userName: username -> fullName -> firstName -> email
          const clerkUserName = clerkUser.username ?? clerkUser.fullName ?? clerkUser.firstName ?? (clerkUser.emailAddresses.length > 0 ? clerkUser.emailAddresses[0].emailAddress : null);
          newUserInfo = {
            userName: newUserInfo.userName ?? clerkUserName,
            email: newUserInfo.email ?? (clerkUser.emailAddresses.length > 0 ? clerkUser.emailAddresses[0].emailAddress : null),
            imageUrl: newUserInfo.imageUrl ?? (clerkUser.externalAccounts.length > 0 ? clerkUser.externalAccounts[0].imageUrl : null),
            fullName: newUserInfo.fullName ?? clerkUser.fullName
          };
        }

        // Only update if userInfo actually changed
        const hasChanged = !userInfo || userInfo.userName !== newUserInfo.userName || userInfo.email !== newUserInfo.email || userInfo.imageUrl !== newUserInfo.imageUrl || userInfo.fullName !== newUserInfo.fullName;
        if (hasChanged) {
          setUserInfo(newUserInfo);
          // Update global store
          globalStore.setCurrentUser(firebaseUser.uid, newUserInfo);
          console.log('[auth] ✅ Updated user info:', {
            email: newUserInfo.email,
            fullName: newUserInfo.fullName,
            hasAvatar: !!newUserInfo.imageUrl
          });
        }
        requestSentryNextjsUserIdentity(firebaseUser.uid, newUserInfo.fullName ?? newUserInfo.userName);
      } catch (error_5) {
        console.error('[auth] ❌ Failed to update user info:', error_5);
      }
    };
    updateUserInfo();
  }, [firebaseUser, clerkUser, userInfo, isAuthReady]);
  return <AuthContext.Provider value={{
    firebaseUser: firebaseUser ?? null,
    userInfo,
    login,
    logout,
    isLoggingIn,
    isLoggingOut,
    tokenVerified,
    isAuthReady
  }}>
      {children}
    </AuthContext.Provider>;
}